/**
 * Where the toast stack sits while a modal is open.
 *
 * Toasts sit top-right. While a modal or dialog is open the stack must never
 * cover that dialog's title row, close button, body controls or footer
 * buttons, at any width (touch has no hover-to-pause). Every box is measured
 * from the DOM:
 *   - the open dialog is the last visible `[aria-modal="true"]` element (the
 *     one on top); when that element is a full-screen backdrop, its panel is
 *     the child that holds the title;
 *   - its title row is the `[data-modal-header]` element, else the first box
 *     no taller than `MODAL_HEADER_MAX_HEIGHT` on the path to the
 *     `aria-labelledby` title;
 *   - its footer row is the `[data-modal-footer]` element, if any.
 *
 * Placement, in order:
 *   1. the stack already clears the dialog (beside it, or above it at its
 *      normal top): leave it alone;
 *   2. it fits above the dialog box: put it there;
 *   3. it fits below the dialog box: put it there;
 *   4. otherwise (a tall modal): just below the title row, over the scrolling
 *      body, ending above the footer row.
 * With no dialog open nothing changes.
 */

type RectLike = { top: number; bottom: number; left: number; right: number; height: number };

/** The slice of the DOM Element API the measurement uses. */
export interface ModalNode {
  getBoundingClientRect(): RectLike;
  getClientRects(): { length: number };
  getAttribute(name: string): string | null;
  querySelector(selector: string): ModalNode | null;
  contains(other: ModalNode): boolean;
  readonly children: ArrayLike<ModalNode>;
}

export interface ModalDocument {
  querySelectorAll(selector: string): ArrayLike<ModalNode>;
  getElementById(id: string): ModalNode | null;
}

/** The open dialog's measured boxes. */
export type OpenModal = { dialog: RectLike; header: RectLike | null; footer: RectLike | null };

/** The toast stack's horizontal extent and total height (position-independent). */
export type StackBox = { left: number; right: number; height: number };

/** The slice of the DOM the stack measurement uses: the toast stack container. */
export interface StackContainer {
  getBoundingClientRect(): { left: number; top: number };
  readonly children: ArrayLike<{ offsetLeft: number; offsetTop: number; offsetWidth: number; offsetHeight: number }>;
}

/**
 * The toast stack's layout box. Uses the toast items' offset boxes, which
 * ignore CSS transforms, so a toast still sliding in from the right (its entry
 * animation starts at `translateX(100%)`) is measured where it will settle,
 * not where it is painted. The container itself is never transformed.
 */
export const measureStackBox = (container: StackContainer): StackBox | null => {
  const items = Array.from(container.children);
  if (items.length === 0) return null;
  const origin = container.getBoundingClientRect();
  const left = Math.min(...items.map((item) => item.offsetLeft));
  const right = Math.max(...items.map((item) => item.offsetLeft + item.offsetWidth));
  const top = Math.min(...items.map((item) => item.offsetTop));
  const bottom = Math.max(...items.map((item) => item.offsetTop + item.offsetHeight));
  return { left: origin.left + left, right: origin.left + right, height: bottom - top };
};

/** Tallest box still treated as a header row (title, subtitle, close button). */
export const MODAL_HEADER_MAX_HEIGHT = 160;
/** Space kept between the stack and the dialog, its rows, or the screen edge. */
export const TOAST_GAP = 8;
/** The toast stack's normal top padding (`p-6`). */
export const TOAST_STACK_DEFAULT_TOP = 24;

const childHolding = (node: ModalNode, target: ModalNode): ModalNode | undefined =>
  Array.from(node.children).find((child) => child.contains(target));

/** The open dialog on top and its title and footer rows, or null when none is open. */
export const findOpenModal = (doc: ModalDocument, viewportHeight: number): OpenModal | null => {
  const dialogs = Array.from(doc.querySelectorAll('[aria-modal="true"]')).filter(
    (dialog) => dialog.getClientRects().length > 0
  );
  const root = dialogs[dialogs.length - 1];
  if (!root) return null;

  const titleId = root.getAttribute("aria-labelledby")?.trim().split(/\s+/)[0];
  const titleEl = titleId ? doc.getElementById(titleId) : null;
  const title = titleEl && root.contains(titleEl) ? titleEl : null;
  let header = root.querySelector("[data-modal-header]");
  if (!header && title) {
    let node: ModalNode = root;
    for (;;) {
      const next = childHolding(node, title);
      if (!next) break;
      if (next === title || next.getBoundingClientRect().height <= MODAL_HEADER_MAX_HEIGHT) {
        header = next;
        break;
      }
      node = next;
    }
  }

  // A full-screen backdrop carrying aria-modal: the panel is the child that
  // holds the title row.
  let panel: ModalNode = root;
  const anchor = header ?? title;
  while (anchor) {
    const box = panel.getBoundingClientRect();
    if (box.top > 0 || box.bottom < viewportHeight) break;
    const next = childHolding(panel, anchor);
    if (!next || next === anchor) break;
    panel = next;
  }

  const footer = root.querySelector("[data-modal-footer]");
  return {
    dialog: panel.getBoundingClientRect(),
    header: header ? header.getBoundingClientRect() : null,
    footer: footer ? footer.getBoundingClientRect() : null,
  };
};

/**
 * The stack's top edge for an open dialog, or null to keep the normal layout.
 * See the placement order at the top of this file.
 */
export const placeToastStack = (
  modal: OpenModal | null,
  stack: StackBox | null,
  viewportHeight: number
): number | null => {
  if (!modal || !stack || stack.height <= 0) return null;
  const { dialog, header, footer } = modal;

  const beside = stack.right <= dialog.left || stack.left >= dialog.right;
  if (beside || TOAST_STACK_DEFAULT_TOP + stack.height <= dialog.top - TOAST_GAP) return null;

  const above = dialog.top - TOAST_GAP - stack.height;
  if (above >= TOAST_GAP) return Math.round(above);

  const below = dialog.bottom + TOAST_GAP;
  if (below + stack.height <= viewportHeight - TOAST_GAP) return Math.round(below);

  // A tall modal: just under the title row. If the stack is too tall to end
  // above the footer row from there, it moves up only as far as keeps the
  // footer clear.
  const underHeader = Math.max(header ? header.bottom : dialog.top, 0) + TOAST_GAP;
  const lowestTop = (footer ? footer.top : viewportHeight) - TOAST_GAP - stack.height;
  return Math.round(Math.max(Math.min(underHeader, lowestTop), TOAST_GAP));
};

/**
 * Re-measures whenever `observe` reports a change and calls `onChange` only
 * when the stack top actually moves (a modal opening, moving, or closing while
 * a toast is up, or the stack growing). Returns the unsubscribe.
 */
export const watchToastStackTop = (
  measure: () => number | null,
  onChange: (top: number | null) => void,
  observe: (notify: () => void) => () => void
): (() => void) => {
  let current: number | null | undefined;
  const check = (): void => {
    const next = measure();
    if (next !== current) {
      current = next;
      onChange(next);
    }
  };
  const unobserve = observe(check);
  check();
  return unobserve;
};
